/**
 * useAdminConfig Hook
 * ===================
 *
 * Fetches admin dashboard configuration from backend
 * Provides SLA thresholds, form defaults, and other dashboard settings
 *
 * Values from database override static defaults in ADMIN_DASHBOARD_CONFIG
 *
 * Usage:
 * ```tsx
 * const { config, loading, error } = useAdminConfig();
 *
 * if (loading) return <div>Loading config...</div>;
 *
 * const slaStatus = hours > config.sla.breachedHours ? 'BREACHED' : 'NORMAL';
 * ```
 */

import { useState, useEffect } from 'react';
import apiService from '@/services/api';
import { ADMIN_DASHBOARD_CONFIG, AdminDashboardConfig } from '@/config/adminDashboard';

// Global cache to avoid repeated API calls
let cachedAdminConfig: AdminDashboardConfig | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Merges API response (flat key-value pairs) with static config structure
 */
function mergeConfigWithDefaults(apiData: Record<string, any>): AdminDashboardConfig {
  return {
    sla: {
      normalHours: Number(apiData.sla_normal_hours ?? ADMIN_DASHBOARD_CONFIG.sla.normalHours),
      criticalHours: Number(apiData.sla_critical_hours ?? ADMIN_DASHBOARD_CONFIG.sla.criticalHours),
      breachedHours: Number(apiData.sla_breached_hours ?? ADMIN_DASHBOARD_CONFIG.sla.breachedHours),
    },
    disputes: {
      urgentResponseWindowHours: Number(apiData.dispute_urgent_response_hours ?? ADMIN_DASHBOARD_CONFIG.disputes.urgentResponseWindowHours),
      performanceWindowDays: Number(apiData.dispute_performance_window_days ?? ADMIN_DASHBOARD_CONFIG.disputes.performanceWindowDays),
    },
    forms: {
      defaultDiscountType: apiData.default_discount_type ?? ADMIN_DASHBOARD_CONFIG.forms.defaultDiscountType,
      defaultAdminRole: apiData.default_admin_role ?? ADMIN_DASHBOARD_CONFIG.forms.defaultAdminRole,
      defaultSuspensionReason: apiData.default_suspension_reason ?? ADMIN_DASHBOARD_CONFIG.forms.defaultSuspensionReason,
      defaultEscalationUrgency: apiData.default_escalation_urgency ?? ADMIN_DASHBOARD_CONFIG.forms.defaultEscalationUrgency,
    },
    messages: ADMIN_DASHBOARD_CONFIG.messages,
    payoutTabs: ADMIN_DASHBOARD_CONFIG.payoutTabs,
  };
}

/**
 * Hook: Fetch and use admin dashboard configuration
 */
export function useAdminConfig() {
  const [config, setConfig] = useState<AdminDashboardConfig>(ADMIN_DASHBOARD_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Check cache first
        if (cachedAdminConfig && Date.now() - cacheTimestamp < CACHE_DURATION) {
          setConfig(cachedAdminConfig);
          setLoading(false);
          return;
        }

        const response = await apiService.get('/admin/config');

        if (response.success && response.data) {
          const merged = mergeConfigWithDefaults(response.data);
          cachedAdminConfig = merged;
          cacheTimestamp = Date.now();
          setConfig(merged);
          setError(null);
        } else {
          throw new Error('Failed to fetch admin config');
        }
      } catch (err) {
        console.error('❌ Admin config error:', err);

        // Use static defaults as fallback
        setConfig(ADMIN_DASHBOARD_CONFIG);
        setError('Using default configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return {
    config,
    loading,
    error,
  };
}

/**
 * Invalidate cache (call after admin updates config)
 */
export function invalidateAdminConfigCache() {
  cachedAdminConfig = null;
  cacheTimestamp = 0;
}

export default useAdminConfig;
